import { useState, useMemo, useCallback } from 'react';

/**
 * useSortPage — unified sort + pagination state for client-side tables.
 *
 * Usage:
 *   const { sortBy, sortDir, page, pageSize, handleSort, setPage, setPageSize,
 *           resetPage, sortedData, paginatedData, paginationProps } = useSortPage(options);
 *
 * Options:
 *   defaultSort   – string, initial sort field (default null = no initial sort)
 *   defaultDir    – 'asc' | 'desc' (default 'asc')
 *   defaultPage   – number (default 1)
 *   defaultSize   – number (default 25)
 */
export default function useSortPage({
  defaultSort = null,
  defaultDir = 'asc',
  defaultPage = 1,
  defaultSize = 25,
} = {}) {
  const [sortBy, setSortBy] = useState(defaultSort);
  const [sortDir, setSortDir] = useState(defaultDir);
  const [page, setPageInternal] = useState(defaultPage);
  const [pageSize, setPageSizeInternal] = useState(defaultSize);

  /** Toggle sort on a field; resets page to 1. */
  const handleSort = useCallback((field) => {
    setSortBy((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortDir('asc');
      }
      return field;
    });
    setPageInternal(1);
  }, []);

  /** Reset to page 1 (call when filters change). */
  const resetPage = useCallback(() => setPageInternal(1), []);

  const setPage = useCallback((p) => setPageInternal(p), []);

  const setPageSize = useCallback((s) => {
    setPageSizeInternal(s);
    setPageInternal(1);
  }, []);

  /**
   * Sort a data array client-side.
   * Handles strings (locale-aware), numbers, and ISO date strings.
   * Pass a getField fn if your field isn't a simple row[sortBy] lookup.
   */
  const sortedData = useCallback(
    (data, getField) => {
      if (!sortBy || !data?.length) return data ?? [];
      const dir = sortDir === 'asc' ? 1 : -1;
      return [...data].sort((a, b) => {
        const av = getField ? getField(a, sortBy) : a?.[sortBy];
        const bv = getField ? getField(b, sortBy) : b?.[sortBy];
        if (av == null && bv == null) return 0;
        if (av == null) return 1 * dir;
        if (bv == null) return -1 * dir;
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
        return (
          String(av).localeCompare(String(bv), undefined, {
            numeric: true,
            sensitivity: 'base',
          }) * dir
        );
      });
    },
    [sortBy, sortDir]
  );

  /**
   * Paginate a (already sorted) array, returning the current page's slice.
   */
  const paginatedData = useCallback(
    (data) => {
      if (!data?.length) return [];
      const start = (page - 1) * pageSize;
      return data.slice(start, start + pageSize);
    },
    [page, pageSize]
  );

  /**
   * Convenience object to spread onto <TablePagination>.
   * total must be provided by the caller (length of the filtered+sorted array).
   */
  const paginationProps = useCallback(
    (total) => ({ page, pageSize, total, onPageChange: setPage, onPageSizeChange: setPageSize }),
    [page, pageSize, setPage, setPageSize]
  );

  return {
    sortBy,
    sortDir,
    page,
    pageSize,
    handleSort,
    resetPage,
    setPage,
    setPageSize,
    sortedData,
    paginatedData,
    paginationProps,
  };
}
